import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { MAX_GRAPHQL_UPLOAD_BYTES, MAX_METADATA_IMPORT_UPLOAD_MB } from '@aline/common';
import { useApolloClient } from '@apollo/client/react';
import type { DocumentMetadataImportFieldsFragment } from 'src/gql/generated/apollo-types';
import * as Apollo from 'src/gql/generated/apollo-types';
import { DocumentMetadataImportStatus } from 'src/gql/generated/apollo-types';
import { useDispatchSuccessSnack } from 'src/helpers';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';

import {
  buildOptimisticConfirmResponse,
  DISPLAY_NAME_FRAGMENT,
  downloadSpreadsheetFile,
  getCanConfirmImportUpload,
  getConfirmImportErrorMessage,
  getDefaultOverwriteQuestionIds,
  getIsImportConfirmValidationError,
  getQuestionIdsMatch,
  getWizardResumeState,
  PREVIEW_STEP,
  SPREADSHEET_MIME_TYPE,
} from './wizard-import.helpers';
import { initWizardState, NAME_REQUIRED_ERROR, wizardReducer } from './wizard-reducer';
import { addImportToHistoryList } from '../data-migration-status';

interface UseImportDataWizardOptions {
  onClose: () => void;
  open: boolean;
  stepCount: number;
  existingImport?: DocumentMetadataImportFieldsFragment | null;
  initialOperationError?: string | null;
  onConfirmValidationError?: (options: {
    errorMessage: string;
    importRecord: DocumentMetadataImportFieldsFragment;
  }) => void;
}

export function useImportDataWizard({
  existingImport,
  initialOperationError = null,
  onClose,
  onConfirmValidationError,
  open,
  stepCount,
}: UseImportDataWizardOptions) {
  const client = useApolloClient();
  const storeDispatch = useNotificationDispatch();

  const [state, dispatch] = useReducer(wizardReducer, existingImport, initWizardState);
  const {
    activeStep,
    displayName,
    draftImportId,
    generatedTemplateQuestionIds,
    operationError,
    overwriteOverride,
    selectedQuestionIds,
    uploadResult,
    uploadedFile,
  } = state;

  const uploadCancelRequested = useRef(false);
  const resumedDraftId = useRef<string | null>(null);

  /**
   * Render-time adjustment instead of an effect so a dismissed error stays
   * dismissed: effects re-run when a hidden Activity tab becomes visible,
   * but this only fires when the prop value actually changes.
   */
  const [prevInitialOperationError, setPrevInitialOperationError] = useState(initialOperationError);
  if (initialOperationError !== prevInitialOperationError) {
    setPrevInitialOperationError(initialOperationError);
    if (initialOperationError)
      dispatch({ error: initialOperationError, type: 'operationErrorSet' });
  }

  // --- Preview (lightweight, server-aggregated) ---

  const shouldLoadPreview = activeStep === PREVIEW_STEP && Boolean(uploadResult);
  const {
    data: previewData,
    error: importPreviewError,
    loading: importPreviewLoading,
  } = Apollo.useDocumentMetadataImportPreviewQuery({
    fetchPolicy: 'cache-and-network',
    skip: !shouldLoadPreview,
    variables: { importId: uploadResult?.id ?? '' },
  });
  const importPreview = previewData?.documentMetadataImportPreview ?? null;

  // --- Derived values (never stored) ---

  const hasCurrentTemplate = getQuestionIdsMatch({
    generatedQuestionIds: generatedTemplateQuestionIds,
    selectedQuestionIds,
  });
  const defaultOverwriteQuestionIds = useMemo(
    () => getDefaultOverwriteQuestionIds(importPreview),
    [importPreview]
  );
  const overwriteQuestionIds = overwriteOverride ?? defaultOverwriteQuestionIds;

  // --- Intent-named state transitions ---

  const clearOperationError = useCallback(() => {
    dispatch({ error: null, type: 'operationErrorSet' });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'stepReversed' });
  }, []);

  const goNext = useCallback(() => {
    dispatch({ stepCount, type: 'stepAdvanced' });
  }, [stepCount]);

  const reset = useCallback(() => {
    uploadCancelRequested.current = false;
    dispatch({ type: 'reset' });
  }, []);

  const resumeFromExisting = useCallback((existing: DocumentMetadataImportFieldsFragment) => {
    uploadCancelRequested.current = false;
    dispatch({ resumeState: getWizardResumeState(existing), type: 'resumed' });
  }, []);

  useEffect(() => {
    if (!open) {
      resumedDraftId.current = null;
      return;
    }
    if (!existingImport) return;
    if (resumedDraftId.current === existingImport.id) return;
    resumedDraftId.current = existingImport.id;
    resumeFromExisting(existingImport);
  }, [existingImport, open, resumeFromExisting]);

  const selectQuestions = useCallback((questionIds: string[]) => {
    uploadCancelRequested.current = false;
    dispatch({ questionIds, type: 'questionsSelected' });
  }, []);

  const updateDisplayName = useCallback(
    (value: string) => {
      dispatch({ displayName: value, type: 'displayNameChanged' });
      if (draftImportId) {
        client.cache.writeFragment({
          data: { displayName: value },
          fragment: DISPLAY_NAME_FRAGMENT,
          id: client.cache.identify({ __typename: 'DocumentMetadataImport', id: draftImportId }),
        });
      }
    },
    [client.cache, draftImportId]
  );

  const setOverwriteQuestionIds = useCallback((questionIds: Set<string>) => {
    dispatch({ overwriteOverride: questionIds, type: 'overwriteOverrideSet' });
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (file.type !== SPREADSHEET_MIME_TYPE) {
        storeDispatch({
          payload: { message: 'Upload a valid .xlsx spreadsheet.', variant: 'error' },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      if (file.size > MAX_GRAPHQL_UPLOAD_BYTES) {
        storeDispatch({
          payload: {
            message: `File exceeds the ${MAX_METADATA_IMPORT_UPLOAD_MB} MB size limit.`,
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return;
      }

      uploadCancelRequested.current = false;
      dispatch({ file, type: 'fileSelected' });
    },
    [storeDispatch]
  );

  const clearUploadedFile = useCallback((uploading: boolean) => {
    if (uploading) {
      uploadCancelRequested.current = true;
      return;
    }
    dispatch({ type: 'fileCleared' });
  }, []);

  const withOperationError = useCallback(
    async (fallbackMessage: string, fn: () => Promise<void>) => {
      try {
        dispatch({ error: null, type: 'operationErrorSet' });
        await fn();
      } catch (error) {
        dispatch({
          error: error instanceof Error ? error.message : fallbackMessage,
          type: 'operationErrorSet',
        });
      }
    },
    []
  );

  // --- Async commands ---

  const [generateTemplate, { loading: generating }] =
    Apollo.useGenerateMetadataImportTemplateMutation();
  const [uploadImport, { loading: uploading }] = Apollo.useUploadMetadataImportMutation();
  const [confirmImportMutation] = Apollo.useConfirmMetadataImportMutation();
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const [closeWhileConfirmingOpen, setCloseWhileConfirmingOpen] = useState(false);

  const dispatchImportStartedSnack = useDispatchSuccessSnack({
    message: 'Data migration started.',
  });

  const dispatchConfirmImportError = useCallback(
    (error: Error) => {
      storeDispatch({
        payload: { message: error.message, variant: 'error' },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      console.error(error);
    },
    [storeDispatch]
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const requestClose = useCallback(() => {
    if (confirmInFlight) {
      setCloseWhileConfirmingOpen(true);
      return;
    }
    handleClose();
  }, [confirmInFlight, handleClose]);

  const dismissCloseWhileConfirming = useCallback(() => {
    setCloseWhileConfirmingOpen(false);
  }, []);

  const closeWizardWhileConfirming = useCallback(() => {
    setCloseWhileConfirmingOpen(false);
    handleClose();
  }, [handleClose]);

  const handleDownload = useCallback(
    async (options: { continueToUpload: boolean }) => {
      const { continueToUpload } = options;
      await withOperationError('Failed to generate template.', async () => {
        const { data } = await generateTemplate({
          update: (cache, result) => {
            const generated = result.data?.generateMetadataImportTemplate.documentMetadataImport;
            if (generated) addImportToHistoryList(cache, generated);
          },
          variables: {
            displayName: displayName.trim() || undefined,
            extractionQuestionIds: selectedQuestionIds,
            importId: draftImportId ?? undefined,
            prefill: false,
          },
        });
        if (!data) return;
        const generatedImport = data.generateMetadataImportTemplate.documentMetadataImport;

        dispatch({
          draftImportId: generatedImport.id,
          questionIds: selectedQuestionIds,
          type: 'templateGenerated',
        });
        downloadSpreadsheetFile(data.generateMetadataImportTemplate);

        if (continueToUpload) goNext();
      });
    },
    [displayName, draftImportId, generateTemplate, goNext, selectedQuestionIds, withOperationError]
  );

  const handleDownloadTemplate = useCallback(() => {
    void handleDownload({ continueToUpload: false });
  }, [handleDownload]);

  const handleDownloadTemplateAndContinue = useCallback(() => {
    if (draftImportId && hasCurrentTemplate) {
      goNext();
      return;
    }
    void handleDownload({ continueToUpload: true });
  }, [draftImportId, goNext, handleDownload, hasCurrentTemplate]);

  const handleUploadAndValidate = useCallback(async () => {
    if (uploadResult) {
      goNext();
      return;
    }
    if (!uploadedFile) {
      storeDispatch({
        payload: {
          message: 'Upload a spreadsheet before validating it.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }
    if (!draftImportId) {
      storeDispatch({
        payload: {
          message: 'Generate a template before uploading your spreadsheet.',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
      return;
    }

    const currentFile = uploadedFile;
    if (!displayName.trim()) {
      dispatch({ error: NAME_REQUIRED_ERROR, type: 'operationErrorSet' });
      return;
    }

    await withOperationError('Failed to validate spreadsheet.', async () => {
      const { data } = await uploadImport({
        update: (cache, result) => {
          const validated = result.data?.uploadMetadataImport.documentMetadataImport;
          if (validated) addImportToHistoryList(cache, validated);
        },
        variables: {
          displayName: displayName.trim(),
          extractionQuestionIds: selectedQuestionIds,
          file: currentFile,
          importId: draftImportId,
        },
      });
      if (!data) return;
      const validatedImport = data.uploadMetadataImport.documentMetadataImport;
      if (uploadCancelRequested.current) return;

      dispatch({ type: 'uploadValidated', uploadResult: validatedImport });
      goNext();
    });
  }, [
    displayName,
    draftImportId,
    goNext,
    selectedQuestionIds,
    storeDispatch,
    uploadImport,
    uploadResult,
    uploadedFile,
    withOperationError,
  ]);

  const handleConfirmImport = useCallback(async () => {
    if (!uploadResult) return;
    if (!getCanConfirmImportUpload(uploadResult)) return;

    const importId = uploadResult.id;
    dispatch({ error: null, type: 'operationErrorSet' });
    setConfirmInFlight(true);

    try {
      const { data } = await confirmImportMutation({
        errorPolicy: 'none',
        optimisticResponse: buildOptimisticConfirmResponse(uploadResult),
        variables: { importId, overwriteQuestionIds: Array.from(overwriteQuestionIds) },
      });
      const confirmedImport = data?.confirmMetadataImport.documentMetadataImport;
      if (!confirmedImport) throw new Error('Failed to start import.');

      if (confirmedImport.status === DocumentMetadataImportStatus.Canceled) {
        storeDispatch({
          payload: {
            message:
              confirmedImport.failedReason ??
              'No changes to apply. Existing document metadata was kept.',
            variant: 'info',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        handleClose();
        return;
      }

      if (confirmedImport.status === DocumentMetadataImportStatus.Failed) {
        dispatch({ type: 'uploadMarkedFailed' });
        dispatch({
          error: confirmedImport.failedReason ?? 'Import failed to start.',
          type: 'operationErrorSet',
        });
        return;
      }

      dispatchImportStartedSnack();
      handleClose();
    } catch (error: unknown) {
      // Apollo rolls back the optimistic update; surface the error to the user.
      const confirmError = getConfirmImportErrorMessage(error);
      dispatchConfirmImportError(confirmError);
      dispatch({ error: confirmError.message, type: 'operationErrorSet' });
      if (getIsImportConfirmValidationError(error)) {
        onConfirmValidationError?.({
          errorMessage: confirmError.message,
          importRecord: uploadResult,
        });
      }
    } finally {
      setConfirmInFlight(false);
    }
  }, [
    confirmImportMutation,
    dispatchConfirmImportError,
    dispatchImportStartedSnack,
    handleClose,
    onConfirmValidationError,
    overwriteQuestionIds,
    storeDispatch,
    uploadResult,
  ]);

  return {
    activeStep,
    clearOperationError,
    clearUploadedFile,
    closeWhileConfirmingOpen,
    closeWizardWhileConfirming,
    confirming: confirmInFlight,
    dismissCloseWhileConfirming,
    displayName,
    generating,
    goBack,
    handleConfirmImport,
    handleDownloadTemplate,
    handleDownloadTemplateAndContinue,
    handleFileSelect,
    handleUploadAndValidate,
    importPreview,
    importPreviewError,
    importPreviewLoading,
    operationError,
    overwriteQuestionIds,
    requestClose,
    selectQuestions,
    selectedQuestionIds,
    setOverwriteQuestionIds,
    updateDisplayName,
    uploadResult,
    uploadedFile,
    uploading,
  };
}
